import { Avatar, BrandLogo, cn, Input } from '@bs/ui-components'
import { LogOut, Menu, Search, Settings, X } from 'lucide-react'
import { FormEvent, useEffect, useRef, useState } from 'react'
import { Link, NavLink, useNavigate } from 'react-router-dom'

import { useAuth } from '@/app/use-auth'
import { useAccount } from '@/app/use-account'
import { suggestJobKeys } from '@/features/jobs/api'

const navigation = [
  { label: 'Jobs', to: '/jobs' },
  { label: 'Activities', to: '/activities' },
  { label: 'My Profile', to: '/my-profile' },
]

export function AppHeader() {
  const { logout } = useAuth()
  const [open, setOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [query, setQuery] = useState('')
  // Legacy SearchboxContainer: job-title suggestions under the box from two characters on.
  const [suggestions, setSuggestions] = useState<string[]>([])
  useEffect(() => {
    const term = query.trim()
    if (term.length < 2) {
      setSuggestions([])
      return
    }
    let cancelled = false
    const timer = setTimeout(() => {
      suggestJobKeys(term)
        .then(({ keys }) => {
          if (!cancelled) setSuggestions(keys)
        })
        .catch(() => {
          if (!cancelled) setSuggestions([])
        })
    }, 200)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [query])
  const userMenuRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { account } = useAccount()

  useEffect(() => {
    if (!userMenuOpen) return

    function closeUserMenu(event: PointerEvent) {
      if (!userMenuRef.current?.contains(event.target as Node)) {
        setUserMenuOpen(false)
      }
    }

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setUserMenuOpen(false)
    }

    document.addEventListener('pointerdown', closeUserMenu)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeUserMenu)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [userMenuOpen])

  function submitSearch(event: FormEvent) {
    event.preventDefault()
    navigate(`/job-search${query.trim() ? `?term=${encodeURIComponent(query.trim())}` : ''}`)
    setOpen(false)
  }

  async function handleLogout() {
    setUserMenuOpen(false)
    setIsLoggingOut(true)
    await logout()
  }

  // The auth session carries authorization (userId, grants), never display fields — name/email
  // come from `GET /v1/me` (`account`). `user` from `useAuth()` is the sign-in check, not the
  // source of a name or email.
  // Empty until the account loads — a blank header beats an invented name in it.
  const displayName = account ? [account.name.first, account.name.last].filter(Boolean).join(' ') : ''
  const email = account?.email ?? ''
  const avatarSrc = account?.avatarUrl ?? ''

  return (
    <header className="relative z-40 h-[92px] bg-white shadow-[0_0_4px_rgba(0,0,0,0.25)]">
      <div className="mx-auto flex h-full max-w-[1460px] items-center gap-5 px-4 sm:px-[30px]">
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="flex size-10 items-center justify-center rounded-full text-primary hover:bg-muted md:hidden"
          aria-label="Toggle navigation"
        >
          {open ? <X /> : <Menu />}
        </button>

        <Link to="/jobs" className="shrink-0">
          <BrandLogo />
        </Link>

        <nav className="hidden h-full items-center gap-8 md:flex">
          {navigation.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'relative flex h-full items-center whitespace-nowrap text-lg text-primary transition hover:text-accent',
                  isActive &&
                    'font-semibold text-accent after:absolute after:inset-x-0 after:bottom-0 after:h-1.5 after:bg-accent',
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <form onSubmit={submitSearch} className="ml-auto hidden max-w-[550px] flex-1 lg:block">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#8a8a8a]" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search jobs, skills or companies"
              className="rounded-full pl-11"
              aria-autocomplete="list"
            />
            {suggestions.length > 0 && query.trim().length >= 2 ? (
              <ul role="listbox" aria-label="Job title suggestions" className="absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-xl border border-border bg-white shadow-lg">
                {suggestions.map((key) => (
                  <li key={key}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={false}
                      className="block w-full px-4 py-2 text-left text-sm text-[#404040] hover:bg-[#f8fbff]"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => {
                        setQuery(key)
                        setSuggestions([])
                        navigate(`/job-search?term=${encodeURIComponent(key)}`)
                      }}
                    >
                      {key}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </form>

        <div ref={userMenuRef} className="relative">
          <button
            type="button"
            onClick={() => setUserMenuOpen((value) => !value)}
            className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
            aria-label="Open account menu"
            aria-haspopup="menu"
            aria-expanded={userMenuOpen}
          >
            <Avatar name={displayName} {...(avatarSrc ? { src: avatarSrc } : {})} className="size-[30px] text-xs" />
          </button>

          {userMenuOpen ? (
            <div
              role="menu"
              className="absolute right-0 top-[calc(100%+12px)] w-60 overflow-hidden rounded-xl border border-border bg-white p-2 shadow-[0_14px_40px_rgba(0,7,79,0.16)]"
            >
              <div className="border-b border-border px-3 py-3">
                <p className="font-semibold text-[#404040]">{displayName}</p>
                <p className="mt-1 truncate text-xs text-muted-foreground">{email}</p>
              </div>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setUserMenuOpen(false)
                  navigate('/account-settings')
                }}
                className="mt-1 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-[#404040] transition hover:bg-muted hover:text-primary"
              >
                <Settings className="size-4" /> Account settings
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => void handleLogout()}
                disabled={isLoggingOut}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-destructive transition hover:bg-destructive/5"
              >
                <LogOut className="size-4" /> {isLoggingOut ? 'Logging out…' : 'Log out'}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {open ? (
        <div className="absolute inset-x-0 top-full border-t border-border bg-primary p-4 text-white shadow-xl md:hidden">
          <form onSubmit={submitSearch} className="relative mb-3">
            <Search className="absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#8a8a8a]" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search jobs"
              className="bg-white pl-11"
            />
          </form>
          <nav className="grid gap-1">
            {navigation.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  cn('rounded-lg px-4 py-3 text-white/75', isActive && 'bg-white/10 text-white')
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      ) : null}
    </header>
  )
}
